import React from "react";
import { makeStyles } from "@material-ui/core";
import { renderRBPayText } from "../../../components/RBPayWordmark";
import { RBPayChoiceCard } from "../../rbPaySettings/components/RBPayChoiceCard";
import { RB_PAY_SETUP_COLORS } from "../../rbPaySettings/constants/rbPaySettings.constants";
import { buildRBPaySetupTheme } from "../../rbPaySettings/utils/rbPaySettings.theme";
import { RB_PAYROLL_SETUP_SELECTION_COPY } from "../constants/rbPayrollOwner.constants";
import {
  RBPayrollDuplicateSource,
  RBPayrollLocationCreationMode,
  RBPayrollLocationCreationModeValue,
  RBPayrollStatus,
} from "../types/rbPayrollOwner.types";

const C = RB_PAY_SETUP_COLORS;

const useStyles = makeStyles({
  card: {
    background: C.cardBg,
    border: `1px solid ${C.cardBorder}`,
    borderRadius: 12,
    padding: "28px 32px 30px",
    boxShadow: "0 1px 5px rgba(26,16,53,0.05)",
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.textDark,
    margin: "0 0 8px",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: 13.5,
    color: C.textLabel,
    lineHeight: 1.6,
    margin: "0 0 24px",
  },
  optionGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
    marginBottom: 20,
    "@media (max-width: 768px)": {
      gridTemplateColumns: "1fr",
    },
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: C.textDark,
    margin: "0 0 12px",
  },
  locationList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: 14,
  },
  locationRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 18px",
    borderRadius: 10,
    cursor: "pointer",
    userSelect: "none",
  },
  locationName: {
    fontSize: 14,
    fontWeight: 600,
    color: C.textDark,
    margin: 0,
    lineHeight: 1.3,
  },
  locationBank: {
    fontSize: 12,
    fontWeight: 500,
    color: C.textLabel,
    margin: "3px 0 0",
    overflowWrap: "anywhere",
  },
  locationSub: {
    fontSize: 12,
    color: C.textMuted,
    margin: "3px 0 0",
  },
  noteBox: {
    padding: "12px 16px",
    background: C.noteBg,
    border: `1px solid ${C.noteBorder}`,
    borderRadius: 8,
  },
  noteText: {
    fontSize: 12.5,
    color: "#6B5F8A",
    lineHeight: 1.55,
    margin: 0,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 999,
    padding: "4px 12px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  errorText: {
    color: C.errorText,
    fontSize: 12.5,
  },
  continueRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 4,
  },
  continueButton: {
    padding: "11px 32px",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
  },
});

interface RBPayrollSetupSelectionProps {
  mode: RBPayrollLocationCreationModeValue;
  selectedLocationId: string;
  sources: RBPayrollDuplicateSource[];
  isSubmitting?: boolean;
  isDuplicateLoading?: boolean;
  primaryColor?: string;
  secondaryColor?: string;
  errorMessage?: string;
  onModeChange: (mode: RBPayrollLocationCreationModeValue) => void;
  onLocationChange: (locationId: string) => void;
  onContinue: () => void;
}

const statusPillStyle = (status: string): React.CSSProperties => {
  const upper = String(status || "").toUpperCase();
  if (upper === RBPayrollStatus.ACTIVE) {
    return { color: C.successText, background: C.successBg, border: `1px solid ${C.successBorder}` };
  }
  if (upper === RBPayrollStatus.PENDING_REVIEW) {
    return { color: C.warnText, background: C.warnBg, border: `1px solid ${C.warnBorder}` };
  }
  if (upper === RBPayrollStatus.REJECTED) {
    return { color: C.errorText, background: "#FEF2F2", border: "1px solid #FECACA" };
  }
  return { color: C.textLabel, background: C.summaryBg, border: `1px solid ${C.summaryBorder}` };
};

const statusLabel = (status: string) => {
  const upper = String(status || "").toUpperCase();
  if (upper === RBPayrollStatus.ACTIVE) return "Active";
  if (upper === RBPayrollStatus.PENDING_REVIEW) return "Pending Review";
  if (upper === RBPayrollStatus.REJECTED) return "Requires Attention";
  return "Not Started";
};

export const RBPayrollSetupSelection: React.FC<RBPayrollSetupSelectionProps> = ({
  mode,
  selectedLocationId,
  sources,
  isSubmitting,
  isDuplicateLoading,
  primaryColor,
  secondaryColor,
  errorMessage,
  onModeChange,
  onLocationChange,
  onContinue,
}) => {
  const classes = useStyles();
  const theme = buildRBPaySetupTheme(primaryColor, secondaryColor);
  const isExisting = mode === RBPayrollLocationCreationMode.COPY;
  const isNew = mode === RBPayrollLocationCreationMode.NEW;
  const busy = Boolean(isSubmitting || isDuplicateLoading);

  return (
    <div className={classes.card}>
      <h1 className={classes.title}>{renderRBPayText(RB_PAYROLL_SETUP_SELECTION_COPY.title)}</h1>
      <p className={classes.subtitle}>
        {renderRBPayText(
          "Since RB Pay is selected for this payment method, choose whether to reuse an existing RB Pay setup or create a new one for this location."
        )}
      </p>

      <div className={classes.optionGrid}>
        <RBPayChoiceCard
          title={RB_PAYROLL_SETUP_SELECTION_COPY.copyTitle}
          description={RB_PAYROLL_SETUP_SELECTION_COPY.copyDescription}
          checked={isExisting}
          disabled={sources.length === 0}
          theme={theme}
          onSelect={() => onModeChange(RBPayrollLocationCreationMode.COPY)}
        />
        <RBPayChoiceCard
          title={RB_PAYROLL_SETUP_SELECTION_COPY.newTitle}
          description={RB_PAYROLL_SETUP_SELECTION_COPY.newDescription}
          checked={isNew}
          theme={theme}
          onSelect={() => onModeChange(RBPayrollLocationCreationMode.NEW)}
        />
      </div>

      {isExisting && sources.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <p className={classes.sectionTitle}>{renderRBPayText("Select an existing RB Pay setup")}</p>
          <div className={classes.locationList}>
            {sources.map((source) => {
              const selected = selectedLocationId === source.locationId;
              return (
                <div
                  key={source.locationId}
                  className={classes.locationRow}
                  style={
                    selected
                      ? { border: `2px solid ${theme.secondary}`, background: theme.secondarySoft }
                      : { border: `1.5px solid ${C.cardBorder}`, background: "#fff" }
                  }
                  onClick={() => onLocationChange(source.locationId)}
                >
                  <div style={{ minWidth: 0 }}>
                    <p className={classes.locationName}>{source.locationName}</p>
                    {source.maskedAccount ? (
                      <p className={classes.locationBank}>Direct Deposit · Account {source.maskedAccount}</p>
                    ) : null}
                    <p className={classes.locationSub}>
                      {source.updatedAt
                        ? `Last updated ${new Date(source.updatedAt).toLocaleDateString()}`
                        : renderRBPayText("Existing RB Pay setup available")}
                    </p>
                  </div>
                  <span className={classes.statusPill} style={statusPillStyle(source.status)}>
                    {statusLabel(source.status)}
                  </span>
                </div>
              );
            })}
          </div>
          <div className={classes.noteBox}>
            <p className={classes.noteText}>
              {renderRBPayText(RB_PAYROLL_SETUP_SELECTION_COPY.copySelectionNote)}
            </p>
          </div>
        </div>
      ) : null}

      {isNew ? (
        <div className={classes.noteBox} style={{ marginBottom: 16 }}>
          <p className={classes.noteText}>{renderRBPayText(RB_PAYROLL_SETUP_SELECTION_COPY.newSelectionNote)}</p>
        </div>
      ) : null}

      {errorMessage ? <div className={classes.errorText}>{errorMessage}</div> : null}

      <div className={classes.continueRow}>
        <button
          type="button"
          className={classes.continueButton}
          style={{ background: theme.primary }}
          onClick={onContinue}
          disabled={busy}
        >
          {busy
            ? isExisting
              ? "Submitting…"
              : "Preparing…"
            : isExisting
            ? "Submit"
            : "Continue"}
        </button>
      </div>
    </div>
  );
};
